import { BigNumber } from '@ethersproject/bignumber'
import { formatUnits } from '@ethersproject/units'
import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { ButtonPrimary } from 'components/Button'
import { MouseoverTooltip } from 'components/Tooltip'
import { TransactionConfirmationContext } from 'contexts/TransactionConfirmationContext'
import { useCurrency } from 'hooks/Tokens'
import { useAllStakedPositionsForIncentive } from 'hooks/useAllStakedPositionsForIncentive'
import { useV3StakerContract } from 'hooks/useContract'
import { useSingleCallResult } from 'lib/hooks/multicall'
import { memo, useCallback, useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled from 'styled-components/macro'
import { Incentive } from 'types/incentives'
import { addHexPrefix } from 'utils/addHexPrefix'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { transactionErrorToUserReadableMessage } from 'utils/transactionErrorToUserReadableMessage'

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-self: flex-end;
  flex-wrap: nowrap;

  @media screen and (max-width: 721px) {
    width: auto;
    justify-content: center;
    align-self: center;
  }
`

const ActionButton = styled(ButtonPrimary)`
  padding: 0 12px;
  font-size: 14px;
  height: 36px;
  min-width: fit-content;
  border-radius: 12px;
  transition: all 0.2s ease-in-out;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: 721px) {
    flex: 0 1 auto;
    min-width: fit-content;
    max-width: none;
  }
`

const UnstakeButton = styled(ActionButton)`
  background-color: ${({ theme }) => theme.accentAction};

  &:hover {
    background-color: ${({ theme }) => theme.accentActionSoft};
    color: ${({ theme }) => theme.accentAction};
    border: 1px solid ${({ theme }) => theme.accentAction};
  }
`

const RefundButton = styled(ActionButton)`
  background-color: ${({ theme }) => theme.accentWarning};

  &:hover {
    background-color: ${({ theme }) => theme.accentWarningSoft};
    color: ${({ theme }) => theme.accentWarning};
    border: 1px solid ${({ theme }) => theme.accentWarningSoft};
  }
`

interface IncentiveActionsProps {
  incentive: Incentive
}

export function IncentiveActions({ incentive }: IncentiveActionsProps) {
  const { account } = useWeb3React()
  const { incentiveId } = useParams<{ incentiveId: string }>()
  const { setTransactionData } = useContext(TransactionConfirmationContext)

  const rewardToken = useCurrency(incentive.key.rewardToken)
  const stakerContract = useV3StakerContract()
  const addTransaction = useTransactionAdder()

  // Get all positions for this incentive
  const { positions, loading } = useAllStakedPositionsForIncentive(incentiveId)

  // Fetch incentive data from the staker contract
  const incentiveResult = useSingleCallResult(stakerContract, 'incentives', [addHexPrefix(incentiveId)])
  const totalRewardUnclaimed: BigNumber | undefined = incentiveResult?.result?.totalRewardUnclaimed
  const numberOfStakes: BigNumber | undefined = incentiveResult?.result?.numberOfStakes

  // Format the totalRewardUnclaimed for display
  const formattedReward = useMemo<string | undefined>(() => {
    if (!totalRewardUnclaimed || !rewardToken) return undefined

    return formatUnits(totalRewardUnclaimed, rewardToken.decimals)
  }, [totalRewardUnclaimed, rewardToken])

  // Determine if refund button should be disabled
  const disableRefund =
    loading ||
    (positions && positions.length > 0) ||
    !stakerContract ||
    !totalRewardUnclaimed ||
    totalRewardUnclaimed.eq(0)

  const handleRefund = useCallback(async () => {
    if (!stakerContract || !account || !incentive?.key) return

    // Set transaction state to attempting
    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Ending incentive and refunding remainder...`,
      hash: '',
      txError: '',
    })

    try {
      // Call the endIncentive function with the incentive key
      const estimatedGas = await stakerContract.estimateGas.endIncentive(incentive.key)

      // Execute the transaction
      const tx = await stakerContract.endIncentive(incentive.key, {
        gasLimit: calculateGasMargin(estimatedGas),
      })

      // Update transaction state with hash
      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      // Add transaction to state for user feedback
      addTransaction(tx, {
        type: TransactionType.CLAIM,
        recipient: incentive.key.refundee,
      })
    } catch (error) {
      console.error('Failed to end incentive:', error)
      setTransactionData((transactionData) => ({
        ...transactionData,
        attempting: false,
        showConfirm: true,
        hash: '',
        txError: transactionErrorToUserReadableMessage(error, 'Unknown error occurred while ending incentive'),
      }))
    }
  }, [stakerContract, account, incentive?.key, setTransactionData, addTransaction])

  return (
    <ActionsContainer>
      {numberOfStakes?.gt(0) ? <UnstakeContainer incentive={incentive} /> : null}
      <MouseoverTooltip
        text={
          totalRewardUnclaimed?.gt(0)
            ? `Refunds the remaining (${formattedReward}) reward tokens. All NFTs must be unstaked first.`
            : 'No unclaimed rewards to refund.'
        }
      >
        <RefundButton onClick={handleRefund} disabled={disableRefund}>
          <Trans>Refund</Trans>
        </RefundButton>
      </MouseoverTooltip>
    </ActionsContainer>
  )
}

const UnstakeContainer = memo(function UnstakeContainer({ incentive }: { incentive: Incentive }) {
  const { account } = useWeb3React()
  const { incentiveId } = useParams<{ incentiveId: string }>()
  const { setTransactionData } = useContext(TransactionConfirmationContext)

  const stakerContract = useV3StakerContract()
  const addTransaction = useTransactionAdder()
  const { positions, loading } = useAllStakedPositionsForIncentive(incentiveId)

  const handleUnstakeNFTs = useCallback(async () => {
    if (!stakerContract || !positions?.length || !account || !incentive?.key) return

    // Ensure we have valid positions with incentive data
    if (!positions[0]?.token0 || !positions[0]?.token1) {
      return
    }

    // Set transaction state to attempting
    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Unstaking ${positions.length} NFTs from incentive...`,
      hash: '',
      txError: '',
    })

    try {
      // Create multicall data for each unstake operation
      const callDatas = positions.map((position) => {
        // For unstaking we need to use the complete incentive key
        return stakerContract.interface.encodeFunctionData('unstakeToken', [
          // Use the complete incentive key object
          incentive.key,
          position.tokenId,
        ])
      })

      // Estimate gas for the multicall
      const estimatedGas = await stakerContract.estimateGas.multicall(callDatas)

      // Execute the multicall transaction
      const tx = await stakerContract.multicall(callDatas, {
        gasLimit: calculateGasMargin(estimatedGas),
      })

      // Update transaction state with hash
      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      // Add transaction to state for user feedback
      addTransaction(tx, {
        type: TransactionType.WITHDRAW_LIQUIDITY_STAKING,
        token0Address: positions[0]?.token0,
        token1Address: positions[0]?.token1,
      })
    } catch (error) {
      console.error('Failed to unstake NFTs:', error)
      setTransactionData((transactionData) => ({
        ...transactionData,
        attempting: false,
        showConfirm: true,
        hash: '',
        txError: transactionErrorToUserReadableMessage(error, 'Unknown error occurred while unstaking'),
      }))
    }
  }, [stakerContract, positions, account, incentive?.key, setTransactionData, addTransaction])

  // Determine if unstake button should be disabled
  const disableUnstake = loading || !positions?.length || !stakerContract

  return (
    <MouseoverTooltip text="This will unstake all NFTs from the incentive.">
      <UnstakeButton onClick={handleUnstakeNFTs} disabled={disableUnstake}>
        <Trans>Unstake NFTs</Trans>
      </UnstakeButton>
    </MouseoverTooltip>
  )
})
